import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star as _Star, ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FavoriteToggle from '../common/FavoriteToggle';
import { buildSrcSet, getResponsiveSizes } from '../../services/assetService';
import { formatCentsToDollars } from '../../utils/formHelpers';
import { deriveExperienceStatus } from '../../utils/experienceStatus';

/**
 * Experience Card Component
 * Displays a single outdoor experience with image carousel
 *
 * @param {Object} experience - Experience data
 * @param {string} variant - Display variant: 'search' (default) or 'guideProfile'
 */
export default function ExperienceCard({ experience, variant = 'search' }) {
  const [currentImage, setCurrentImage] = useState(0);
  const [isFavorited, setIsFavorited] = useState(experience.is_favorited || false);
  const navigate = useNavigate();

  // Variant-specific configuration
  const showGuidePhoto = variant === 'search';
  const badgeTopPosition = variant === 'search' ? 'top-[54px]' : 'top-[14px]';
  const showStatusIndicator = variant === 'guideProfile';

  // MVP 1.27.7-S (SCRUM-784 AC-06): Prefer primaryUrl from assets, fall back to images array, then placeholder
  // eslint-disable-next-line no-secrets/no-secrets
  const placeholderSvg = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2U1ZTdlYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzlhYTBhNiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIFBob3RvPC90ZXh0Pjwvc3ZnPg==';
  const rawImages = experience.primaryUrl
    ? [experience.primaryUrl, ...(experience.images || []).slice(1)]
    : (experience.images || []);
  const images = rawImages.length > 0 ? rawImages : [placeholderSvg];
  // SCRUM-793 AC-01: Build srcSet from asset variants for responsive images
  const srcSet = experience.variants ? buildSrcSet(experience.variants) : null;
  const cardSizes = srcSet ? getResponsiveSizes('card') : undefined;
  const location = typeof experience.location === 'string'
    ? experience.location
    : experience.location?.name || '';
  const rating = typeof experience.rating === 'number'
    ? experience.rating
    : parseFloat(experience.rating?.average) || 0;
  const _reviews = typeof experience.reviews === 'number'
    ? experience.reviews
    : parseInt(experience.rating?.reviewCount) || 0;
  const priceCents = experience.pricing?.basePriceCents ?? experience.base_price_cents ?? null;
  const priceDisplay = priceCents !== null
    ? formatCentsToDollars(priceCents)
    : `$${experience.price || experience.pricing?.basePrice || 0}`;
  const duration = experience.duration || '';

  const handlePreviousImage = (  _e) => {
    _e.stopPropagation();
    _e.preventDefault();
    setCurrentImage(currentImage - 1);
  };

  const handleNextImage = (  _e) => {
    _e.stopPropagation();
    _e.preventDefault();
    setCurrentImage(currentImage + 1);
  };

  // Link to unified experience detail page (shows edit buttons if owner)
  const tripUrl = `/experience/${experience.id}`;

  return (
    <Link to={tripUrl} className="group cursor-pointer block">
      <div className="relative mb-3">
        {/* Guide Photo - Search variant only - Outside overflow container */}
        {showGuidePhoto && experience.guide?.photo && (
          <div className="absolute top-[-8px] left-3 z-10">
            <img
              src={experience.guide.photo}
              alt={experience.guide.name}
              className="w-[70px] h-[70px] rounded-full border-2 border-white shadow-lg object-cover"
            />
          </div>
        )}

        <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
          <img
            src={images[currentImage]}
            srcSet={currentImage === 0 && srcSet ? srcSet : undefined}
            sizes={currentImage === 0 && srcSet ? cardSizes : undefined}
            alt={experience.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />

          {/* Badge/Flag */}
          {experience.badge && (
            <div className={`absolute ${badgeTopPosition} left-0 z-20`}>
              <div
                className="text-white text-center px-4 py-2 text-xs font-semibold shadow-lg uppercase tracking-wide"
                style={{
                  minWidth: '125px',
                  backgroundColor: experience.badge.color === 'bg-orange-500' ? '#f97316' :
                                   experience.badge.color === 'bg-blue-500' ? '#3b82f6' :
                                   experience.badge.color === 'bg-green-500' ? '#22c55e' :
                                   experience.badge.color === 'bg-purple-500' ? '#a855f7' :
                                   experience.badge.color === 'bg-yellow-500' ? '#eab308' :
                                   experience.badge.color === 'bg-red-500' ? '#ef4444' :
                                   '#6b7280',
                  clipPath: 'polygon(0 0, 100% 0, calc(100% - 8px) 50%, 100% 100%, 0 100%)'
                }}
              >
                {experience.badge.text}
              </div>
            </div>
          )}

          <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 flex space-x-1">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={(  _e) => {
                  _e.stopPropagation();
                  setCurrentImage(idx);
                }}
                className={`w-1.5 h-1.5 rounded-full transition ${
                  idx === currentImage ? 'bg-white' : 'bg-white/60'
                }`}
                aria-label={`View image ${idx + 1}`}
              />
            ))}
          </div>

          {currentImage > 0 && (
            <button
              onClick={handlePreviousImage}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition flex items-center justify-center hover:scale-110"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          {currentImage < images.length - 1 && (
            <button
              onClick={handleNextImage}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition flex items-center justify-center hover:scale-110"
              aria-label="Next image"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}

          <div className="absolute top-3 right-3">
            <FavoriteToggle
              itemType="experience"
              itemId={experience.id}
              isFavorited={isFavorited}
              onToggle={(newStatus) => setIsFavorited(newStatus)}
              size="lg"
              className="bg-white/90 hover:bg-white shadow-md"
            />
          </div>

          {/* SCRUM-348, SCRUM-1725: Status indicator using shared deriveExperienceStatus helper */}
          {showStatusIndicator && (() => {
            const derivedStatus = deriveExperienceStatus(experience);
            return (
            <div className="absolute top-3 left-3 flex items-center gap-1.5">
              {derivedStatus === 'published' && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300" data-testid="status-badge">
                  Published
                </span>
              )}
              {derivedStatus === 'draft' && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300" data-testid="status-badge">
                  Draft
                </span>
              )}
              {derivedStatus === 'inactive' && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-300" data-testid="status-badge">
                  Inactive
                </span>
              )}
              {/* SCRUM-827: Edit button for guide's experiences */}
              <button
                onClick={(_e) => {
                  _e.stopPropagation();
                  _e.preventDefault();
                  navigate(`/guide/experiences/${experience.id}/edit`);
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-white/90 text-gray-700 border border-gray-300 hover:bg-white hover:text-emerald-700 shadow-sm transition-colors"
                title="Edit in wizard"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </button>
            </div>
            );
          })()}

          <div className="absolute bottom-0 left-0 right-0 bg-gray-900/75 backdrop-blur-sm p-1.5 sm:p-2 md:p-2 rounded-b-xl">
            <div className="space-y-0.5">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-white text-xs sm:text-sm md:text-base line-clamp-1" title={location}>
                  {location}
                </h3>
                {/* SCRUM-346: Only show rating if there are reviews */}
                {_reviews > 0 && (
                  <div className="flex items-center space-x-0.5 sm:space-x-1 flex-shrink-0">
                    <_Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 fill-current text-white" />
                    <span className="text-xs sm:text-sm font-medium text-white">{rating}</span>
                    <span className="text-xs sm:text-sm text-white/80">({_reviews})</span>
                  </div>
                )}
              </div>
              {/* SCRUM-347: Use line-clamp instead of truncate + add title tooltip */}
              <p className="text-white/90 text-xs sm:text-sm line-clamp-2" title={experience.title}>{experience.title}</p>
              <p className="text-white/80 text-xs sm:text-sm">{duration}</p>
              <div className="pt-0.5">
                <span className="font-semibold text-white text-xs sm:text-sm md:text-base">
                  {priceDisplay}
                </span>
                <span className="text-white/80 text-xs sm:text-sm"> per person</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
